import {createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import type {ListingViewHeader} from 'llm/chat-library/shared/list-detail/listDetailTypes';

const useStyles = createStyles(
  (theme, {wideInset, scrollable}: {wideInset: boolean; scrollable: boolean}) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
    },
    header: {
      paddingTop: wideInset ? theme.spacing.md : theme.spacing.lg,
      // Listings keep 12px under the title so the tabs sit where the agent detail tabs do. A scrolling
      // pane puts the 24px gap on the body instead, so the title and the first row stay 24px apart.
      paddingBottom: wideInset && !scrollable ? theme.spacing.md : theme.spacing.none,
      paddingLeft: wideInset ? theme.spacing['3xl'] : theme.spacing.md,
      paddingRight: wideInset ? theme.spacing['3xl'] : theme.spacing.md,
    },
    scroll: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      paddingTop: wideInset ? theme.spacing.xxl : theme.spacing.md,
      paddingBottom: theme.spacing.md,
      paddingLeft: wideInset ? theme.spacing['3xl'] : theme.spacing.md,
      paddingRight: wideInset ? theme.spacing['3xl'] : theme.spacing.md,
    },
  })
);

export function ChatSettingsPaneHeader({
  header,
  wideInset = false,
  scrollable = true,
}: {
  header: ListingViewHeader;
  /** Match the agent detail column: 32px sides and 12px above the title. */
  wideInset?: boolean;
  /** When false, keep 12px under the title for a tab bar. When true, the body supplies the gap. */
  scrollable?: boolean;
}): JSX.Element {
  const {classes} = useStyles({wideInset, scrollable});

  return (
    <Stack spacing="xs" className={classes.header}>
      <Group noWrap={true} position="apart" align="center">
        <Group noWrap={true} spacing="xs" align="center">
          <Text fw="bold" size="xl">
            {header.title}
          </Text>
          {header.titleBadge}
        </Group>
        {header.action}
      </Group>
    </Stack>
  );
}

export default function ChatSettingsPane({
  header,
  scrollable = true,
  wideInset = false,
  children,
}: {
  header?: ListingViewHeader;
  /** When true (default) the children render inside a scrolling region. */
  scrollable?: boolean;
  /** Match the agent detail column: 32px sides, 12px above the title, and 24px before the body. */
  wideInset?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles({wideInset, scrollable});

  return (
    <div className={classes.root}>
      {header != null && (
        <ChatSettingsPaneHeader header={header} wideInset={wideInset} scrollable={scrollable} />
      )}
      {scrollable ? <div className={classes.scroll}>{children}</div> : children}
    </div>
  );
}
